import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'

import { checkoutTotal, checkoutTotalDiscounted, getActiveStudent, removeStudent } from '@actions/checkout'
import { updateActiveVoucher } from '@actions/activeVoucher.js'
import { activateVoucherForm, activateStudentForm } from '@actions/voucherForm'

// Mini-resumo lateral do fluxo V2 (pedido do Julio: acompanhar o carrinho à direita
// ao longo das etapas, como no V1). Regras preservadas:
// P-37 (remover aluno com confirmação; voucher ativo migra), P-38 (visibilidade do
// "Novo aluno" no fluxo de desconto especial), P-39 (trocar/editar aluno leva à seleção),
// P-41..P-47 (totais, frete B2C, frete grátis, economia), V-46..V-57 via VoucherForm.

const money = v => Number(v || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })

export default function V2CartAside({ cta = null, primary = true, ctaOutside = false }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const checkout = useSelector(state => state.checkout)
  const student = useSelector(state => getActiveStudent(state))
  const totalFrom = useSelector(state => checkoutTotal(state))
  const totalNow = useSelector(state => checkoutTotalDiscounted(state))
  const freight = useSelector(state => state.freight || {})
  const specialDiscounts = useSelector(
    state => (state.specialDiscount && state.specialDiscount.special_discounts) || []
  )

  const [removingIdx, setRemovingIdx] = useState(null)

  const isB2C = checkout.some(s => s.school_is_b2c)
  const freightValue = isB2C ? Number(freight.calculated_freight || 0) : 0
  const savings = totalFrom - totalNow
  const grandTotal = totalNow + freightValue

  // P-38
  const usedDiscountIds = checkout.map(s => s.special_discount_id).filter(Boolean)
  const hasSpecialFlow = checkout.some(s => Number.isInteger(s.special_discount_id))
  const canAddStudent = !hasSpecialFlow || specialDiscounts.some(d => !usedDiscountIds.includes(d.id))

  const _goProducts = () => {
    if (!location.pathname.includes('selecionar-livros')) navigate('/v2/steps/selecionar-livros')
  }

  const _switchStudent = s => {
    dispatch(updateActiveVoucher(s.voucher, s.name)) // P-39
    _goProducts()
  }

  const _editStudent = s => {
    dispatch(updateActiveVoucher(s.voucher, s.name))
    dispatch(activateStudentForm(true)) // abre o StudentForm V1 (V-58..V-61)
    _goProducts()
  }

  const _addStudent = () => {
    dispatch(updateActiveVoucher(null))
    dispatch(activateVoucherForm(true)) // abre o VoucherForm V1 (V-46..V-57)
    _goProducts()
  }

  const _confirmRemove = idx => {
    const removed = checkout[idx]
    dispatch(removeStudent(idx)) // P-37
    setRemovingIdx(null)
    const remaining = checkout.filter((_, i) => i !== idx)
    if (remaining.length && student && removed && student.voucher === removed.voucher && student.name === removed.name) {
      dispatch(updateActiveVoucher(remaining[0].voucher, remaining[0].name))
    }
  }

  // Guia de identidade (set/2026): "Resumo do pedido" como cartão; ele é o cartão principal
  // (borda vermelha) nas etapas em que carrega a ação — no Resumo a borda vai para o pagamento.
  return (
    <aside id="v2-cart-aside" className="v2-aside">
      <div className={`v2-sum${primary ? ' v2-sum--primary' : ''}`}>
        <h2 className="v2-sum__title">Resumo do pedido</h2>

        {checkout.map((s, i) => {
          const isActive = student && s.voucher === student.voucher && s.name === student.name
          const items = (s.products || []).filter(p => p.active)
          return (
            <div key={`${s.voucher}-${i}`} className="v2-sum__student">
              <span className={`v2-sum__num${isActive ? ' is-active' : ''}`}>{i + 1}</span>
              <div className="v2-sum__student-body">
                <p className="v2-sum__name">{s.name}</p>
                <p className="v2-sum__meta">
                  Voucher {s.voucher} · {items.length} itens
                </p>
                <p className="v2-sum__amount">{money(s.total_discounted)}</p>
                <div className="v2-sum__links">
                  {!isActive || !location.pathname.includes('selecionar-livros') ? (
                    <button type="button" className="is-red" onClick={() => _switchStudent(s)}>
                      Ver produtos
                    </button>
                  ) : null}
                  <button type="button" onClick={() => _editStudent(s)}>
                    Editar
                  </button>
                  {checkout.length > 1 && (
                    <button type="button" onClick={() => setRemovingIdx(i)}>
                      Remover
                    </button>
                  )}
                </div>
                {removingIdx === i && (
                  <div className="v2-sum__confirm">
                    Você tem certeza que deseja remover este aluno?
                    <div>
                      <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => _confirmRemove(i)}>
                        Sim
                      </button>
                      <button type="button" className="v2-btn v2-btn--neutral v2-btn--sm" onClick={() => setRemovingIdx(null)}>
                        Não
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        })}

        {canAddStudent && (
          <button id="v2-add-student" type="button" className="v2-sum__add" onClick={_addStudent}>
            + Adicionar outro aluno
          </button>
        )}

        <dl className="v2-sum__lines">
          <div>
            <dt>Subtotal</dt>
            <dd>
              {savings > 0 && <s>de {money(totalFrom)}</s>}
              {money(totalNow)}
            </dd>
          </div>
          {savings > 0 && (
            <div className="is-ok">
              <dt>Você está economizando</dt>
              <dd>− {money(savings)}</dd>
            </div>
          )}
          <div className={isB2C ? '' : 'is-ok'}>
            <dt>Frete</dt>
            <dd>{isB2C ? (freightValue > 0 ? money(freightValue) : 'a calcular') : 'Grátis'}</dd>
          </div>
        </dl>
        <div className="v2-sum__total">
          <span>Total</span>
          <strong>{money(grandTotal)}</strong>
        </div>

        {!ctaOutside && cta ? <div className="v2-sum__cta">{cta}</div> : null}
      </div>

      {ctaOutside ? cta : null}
    </aside>
  )
}
